import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Switch,
  Button,
  Typography,
  MenuItem,
  FormControl,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails, SelectChangeEvent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, IconButton,
  Paper, Alert, OutlinedInput, ListItemText, Checkbox, CircularProgress, TextField, Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import {
  fetchMonitoringConfigsAPI,
  saveMonitoringConfigAPI,
  updateMonitoringConfigAPI,
} from '../operations/monitoringService';
import { fetchAirportsByQueryAPI } from '../operations/airportService';

// -------------------- Styled Switch --------------------
const BlackWhiteSwitches = styled(Switch)(() => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#000',
    '& + .MuiSwitch-track': {
      backgroundColor: '#000',
    },
  },
  '& .MuiSwitch-switchBase': {
    color: '#ccc',
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#ccc',
  },
}));

// -------------------- Select MenuProps --------------------
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const SupplierNames = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
const SupplierSourcesMP = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// -------------------- Types --------------------
interface SupplierOption {
  id: number;
  name: string;
}

interface MonitoringConfig {
  product: string;
  supplierName: string;
  supplierSource: string;
  departure: string;
  arrival: string;
  // frequency: number;
  // feq_smh: string;
  noOfRecords: number;
  recordThreshold: number;
  responseThreshold: number;
  isActive: boolean;
  supplierId?: number;
}

interface Airport {
  code: string;
  fullName: string;
  type?: string;
  countryCode?: string;
}

// -------------------- Component --------------------
const FlightMonitoringSetting: React.FC = () => {
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [alertSeverity, setAlertSeverity] = useState<
  'success' | 'info' | 'warning' | 'error'
>('error'); 
  const [isEdit, setIsEdit] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MonitoringConfig | null>(null);

  const [monitoringData, setMonitoringData] = useState<MonitoringConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const handleCloseModal = () => { setOpenModal(false); };

  // supplier source/name selection + maps
  const [supplierSources] = useState(["API", "AMADEUS"]);
const [supplierSourcesSelected, setSupplierSourcesSelected] = useState<string[]>([]);


const supplierNamesMap: Record<string, string[]> = {
API: ["Mondee", "Mystifly", "Duffel"],
AMADEUS: ["Mondee", "Voyzent", "Wsap"],
};


const [supplierNamesSelected, setSupplierNamesSelected] = useState<string[]>([]);


const sourcesMap: Record<string, SupplierOption[]> = {
    API: [
          { id: 1, name: 'Mondee' },
          { id: 2, name: 'Mystifly' },
          { id: 3, name: 'Duffel' }
        ],
AMADEUS: [
          { id: 0, name: 'Mondee' },
          { id: 0, name: 'Voyzent' },
          { id: 0, name: 'Wsap' }
        ]
};
  const [supplierDetails, setSupplierDetails] = useState<
    { supplierSource: string; supplierName: string; supplierId: number }[]
  >([]);

  // form data
  const [formData, setFormData] = useState<{
    departure: Airport | null;
    arrival: Airport | null;
    // frequency: string | number;
    // feq_smh: string;   
    noOfRecords: string | number;
    responseThreshold: string | number;
    recordThreshold: string | number;
  }>({
    departure: null,
    arrival: null,
    // frequency: '',
    // feq_smh: 'minute',   
    noOfRecords: '',
    responseThreshold: '',
    recordThreshold: '',
  });

  // airport options/loading
  const [departureOptions, setDepartureOptions] = useState<Airport[]>([]);
  const [arrivalOptions, setArrivalOptions] = useState<Airport[]>([]);
  const [loadingDeparture, setLoadingDeparture] = useState(false);
  const [loadingArrival, setLoadingArrival] = useState(false);

  // status/toggle
  const [isActive, setIsActive] = useState<boolean>(true);
  const [toggleChanged, setToggleChanged] = useState<boolean>(false);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

    // -------------------- Airport Fetch --------------------
  const fetchAirports = async (query: string, type: 'departure' | 'arrival') => {
    if (!query || query.length < 3) return;

    try {
      if (type === 'departure') setLoadingDeparture(true);
      if (type === 'arrival') setLoadingArrival(true);

      const response = await fetchAirportsByQueryAPI(query.toUpperCase());

      const airports: Airport[] =
        response?.data?.data && Array.isArray(response.data.data)
          ? response.data.data
          : [];

      if (type === 'departure') setDepartureOptions(airports);
      if (type === 'arrival') setArrivalOptions(airports);
    } catch (err) {
      console.error('Error fetching airports:', err);
      if (type === 'departure') setDepartureOptions([]);
      if (type === 'arrival') setArrivalOptions([]);
    } finally {
      if (type === 'departure') setLoadingDeparture(false);
      if (type === 'arrival') setLoadingArrival(false);
    }
  };

  // -------------------- Inputs --------------------
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = ['noOfRecords', 'responseThreshold', 'recordThreshold'];
    setFormData((prev) => ({
      ...prev,
      [name]: numericFields.includes(name)
      ? value === "" 
        ? ""          
        : Number(value)
      : value,
  }));
  if (value !== "") setInvalidFields((prev) => prev.filter((f) => f !== name));
};

  const updateSupplierDetails = (sources: string[], names: string[]) => {
    const newCombinations: typeof supplierDetails = [];

    sources.forEach((source) => {
      const availableNames = supplierNamesMap[source] || [];
      names
        .filter((name) => availableNames.includes(name))
        .forEach((name) => {
          const supplierObj = sourcesMap[source]?.find((s) => s.name === name);
          if (!supplierObj) return;

          const exists = supplierDetails.some(
            (entry) =>
              entry.supplierSource === source &&
              entry.supplierName === name &&
              entry.supplierId === supplierObj.id
          );
          if (!exists) {
            newCombinations.push({
              supplierSource: source,
              supplierName: name,
              supplierId: supplierObj.id,
            });
          }
        });
    });

    if (newCombinations.length > 0) {
      setSupplierDetails((prev) => [...prev, ...newCombinations]);
    }
  };

  const handleSupplierSourceChange = (e: any) => {
    const selected = e.target.value as string[];
    // find sources that were removed
  const removedSources = supplierSourcesSelected.filter(
    (src) => !selected.includes(src)
  );
 // remove supplier names that belong only to removed sources
  const updatedNames = supplierNamesSelected.filter((name) => {
    // get all sources that provide this supplier
    const supplierSources = Object.keys(supplierNamesMap).filter((src) =>
      (supplierNamesMap[src] || []).includes(name)
    );

    // check if at least one source for this supplier is still selected
    const stillValid = supplierSources.some((src) =>
      selected.includes(src)
    );

    return stillValid; // keep if still valid, otherwise remove
  });
    
    setSupplierSourcesSelected(selected);
    setSupplierNamesSelected(updatedNames);
    updateSupplierDetails(selected, updatedNames);
    if (selected.length > 0)
    setInvalidFields((prev) => prev.filter((f) => f !== "Supplier Source"));
  };

  const handleSupplierNameChange = (e: any) => {
    const selected = e.target.value as string[];
    setSupplierNamesSelected(selected);
    updateSupplierDetails(supplierSourcesSelected, selected);
    if (selected.length > 0)
    setInvalidFields((prev) => prev.filter((f) => f !== "Supplier Name"));
  };

  const handleRemoveDetail = (idx: number) => {
    setSupplierDetails((prev) => prev.filter((_, i) => i !== idx));
  };

  // -------------------- Toggle (tracks user action) --------------------
  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
    if (isEdit) setToggleChanged(true);
  };

   // Live validation and input handling
  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // Live validation: same Departure/Arrival not allowed
      if (
        updated.departure?.code &&
        updated.arrival?.code &&
        updated.departure.code.toUpperCase() === updated.arrival.code.toUpperCase()
      ) {
        setInvalidFields(["departure", "arrival"]);
        setResponseMessage("Departure and Arrival cannot be the same airport.");
        setAlertSeverity("error");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      } else {
        // Clear red border if corrected
        setInvalidFields((prevInvalid) =>
          prevInvalid.filter((f) => f !== "departure" && f !== "arrival")
        );
      }

      return updated;
    });
  };

  // -------------------- Save / Update --------------------
  const handleSave = async () => {
    console.log("handleSave called");

    const isEmpty = (val: any) => val === "" || val === null || val === undefined;
    

     // Map of field names to user-friendly labels
    const fieldLabels: { [k in keyof typeof formData]: string } = {
      departure: "Departure",
      arrival: "Arrival",
      // frequency: "Frequency",
      // feq_smh: "Frequency (SMH)", 
      noOfRecords: "No. of Records",
      responseThreshold: "Response Time (T.H)",
      recordThreshold: "No. of Records (T.H)",
    };

    const missingFields: string[] = [];

    // Check formData fields
    (Object.keys(formData) as (keyof typeof formData)[]).forEach((field) => {
      if (isEmpty(formData[field])) {
        missingFields.push(field);
      }
    });

    // Also check supplier selections
    if (!supplierSourcesSelected.length) missingFields.push("Supplier Source");
    if (!supplierNamesSelected.length) missingFields.push("Supplier Name");

   

    if (missingFields.length >= 1) {
      if (missingFields.length === 1) {
        setInvalidFields(missingFields);
        setResponseMessage(
          isEdit
            ? `${fieldLabels[missingFields[0] as keyof typeof fieldLabels] || missingFields[0]} is missing. Please update the valid input field.`
            : `${fieldLabels[missingFields[0] as keyof typeof fieldLabels] || missingFields[0]} is missing. Please enter the valid input field.`
        );
        setAlertSeverity("error");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
      } else {
      setInvalidFields(missingFields);
      setResponseMessage(
      isEdit
        ? `Please update all valid input fields.`
        : `Please enter all required fields.`
      );
    setAlertSeverity("error");
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
  }
  setShowAlert(true);
      return;
    }
  setInvalidFields([]);

  const numericFields: (keyof typeof formData)[] = [
  // "frequency",
  "noOfRecords",
  "responseThreshold",
  "recordThreshold",
];

const invalidNumericFields = numericFields.filter(
  (field) => Number(formData[field]) <= 0
);

if (invalidNumericFields.length > 0) {
  // Highlight all invalid fields
  setInvalidFields(invalidNumericFields);

  // Create a readable list of labels
  const invalidLabels = invalidNumericFields
    .map((f) => fieldLabels[f as keyof typeof fieldLabels] || f)
    .join(", ");

  // Build message
  const message =
    invalidNumericFields.length > 1
      ? `${invalidLabels} must be greater than zero.`
      : `${invalidLabels} must be greater than zero.`;

  setResponseMessage(message);
  setAlertSeverity("error");
  setShowAlert(true);
  setTimeout(() => setShowAlert(false), 5000);
  return; // stop save
}

const latestSupplierDetails = supplierSourcesSelected.flatMap((source) => {
const availableNames = supplierNamesMap[source] || [];
return supplierNamesSelected
.filter((name) => availableNames.includes(name))
.map((name) => {
const supplierObj = sourcesMap[source]?.find((s) => s.name === name);
return {
supplierSource: source,
supplierName: name,
supplierId: supplierObj?.id ?? 0,
};
});
});

// Final departure/arrival validation before submit
    if (
      formData.departure?.code &&
      formData.arrival?.code &&
      formData.departure.code.toUpperCase() === formData.arrival.code.toUpperCase()
    ) {
      setInvalidFields(["departure", "arrival"]);
      setResponseMessage("Departure and Arrival cannot be the same airport.");
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
      return;
    }


     // Check for duplicates
  const duplicate = latestSupplierDetails.some((detail) =>
    monitoringData.some(
      (existing) =>
        existing.supplierSource === detail.supplierSource &&
        existing.supplierName === detail.supplierName
    )
  );

  if (duplicate && !isEdit) {
    setResponseMessage("This configuration already exists.");
    setAlertSeverity("error");
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 5000);
    return;
  }

    let payload: any = {};

    if (isEdit && editingRecord) {
      // --- UPDATE: only changed fields + mandatory identifiers ---
      payload = {
        product: 'Flight',
        supplierId: editingRecord.supplierId ?? 0,
        supplierName: editingRecord.supplierName,
      };

      let changed = false;

      // compare fields (excluding isActive)
      const fieldsToCheck: (keyof typeof formData)[] = [
        'departure',
        'arrival',
        // 'frequency',
        // 'feq_smh',
        'noOfRecords',
        'responseThreshold',
        'recordThreshold',
      ];

      fieldsToCheck.forEach((field) => {
        const newValueAny = formData[field];
        const oldValueAny = (editingRecord as any)[field];

        // handle departure/arrival (form object vs stored string)
        if (field === 'departure') {
          const newCode = newValueAny ? (newValueAny as Airport).code : '';
          const oldCode = oldValueAny ?? '';
          if (String(newCode) !== String(oldCode)) {
            payload.departure = newCode;
            changed = true;
          }
          return;
        }

        if (field === 'arrival') {
          const newCode = newValueAny ? (newValueAny as Airport).code : '';
          const oldCode = oldValueAny ?? '';
          if (String(newCode) !== String(oldCode)) {
            payload.arrival = newCode;
            changed = true;
          }
          return;
        }

        // normalize (string vs number)
        let newValue = newValueAny;
        let oldValue = oldValueAny;

        if (
          typeof oldValue === 'number' &&
          typeof newValue === 'string' &&
          !isNaN(Number(newValue))
        ) {
          newValue = Number(newValue);
        }

        // Strings/truthy check
        if (String(newValue) !== String(oldValue)) {
          payload[field] = newValue;
          changed = true;
        }
      });

      // include isActive ONLY if the user actually toggled
      if (toggleChanged) {
        payload.isActive = isActive;
        changed = true;
      }

      if (!changed) {
        console.log('No changes detected. Skipping update.');
        setResponseMessage('No changes detected.');
        setAlertSeverity("info");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
        setLoading(false);
        return;
      }

    //   const missingFields: string[] = [];

    // (Object.keys(formData) as (keyof typeof formData)[]).forEach((field) => {
    //   if (isEmpty(formData[field])) {
    //     missingFields.push(field);
    //   }
    // });

    // if (!supplierSourcesSelected.length) missingFields.push("Supplier Source");
    // if (!supplierNamesSelected.length) missingFields.push("Supplier Name");

    //   if (missingFields.length > 0) {
    //   if (missingFields.length === 1) {
    //     setResponseMessage(
    //       `${fieldLabels[missingFields[0] as keyof typeof fieldLabels] || missingFields[0]} is empty.  fields.`
    //     );
    //     setAlertSeverity("error");
    //     setShowAlert(true);
    //     setTimeout(() => setShowAlert(false), 5000);
    //   } else {
    //     setInvalidFields(missingFields);
    //     setResponseMessage(
    //       `These fields are empty: ${missingFields
    //         .map((f) => fieldLabels[f as keyof typeof fieldLabels] || f)
    //         .join(", ")}. Please update them.`
    //     );
    //     setAlertSeverity("error");
    //   setShowAlert(true);
    //     setTimeout(() => setShowAlert(false), 5000);
    //   }
    //   setShowAlert(true);
    //   return;
    // }
    // setInvalidFields([]);
    
    } else {
      // --- CREATE: full payload ---
      payload = {
        product: 'Flight',
        supplierDetails: latestSupplierDetails, // built from selections
        // ...formData,
        departure: formData.departure?.code ?? '',
        arrival: formData.arrival?.code ?? '',
        // frequency: Number(formData.frequency),
        // feq_smh: formData.feq_smh?.toLowerCase() || "minute", 
        noOfRecords: Number(formData.noOfRecords),
        responseThreshold: Number(formData.responseThreshold),
        recordThreshold: Number(formData.recordThreshold),
        isActive,
      };
      
      //  const duplicate = latestSupplierDetails.some((detail) =>
      //   monitoringData.some(
      //     (existing) =>
      //       existing.supplierSource === detail.supplierSource &&
      //       existing.supplierName === detail.supplierName
      //   )
      // );

      // if (duplicate && !isEdit) {
      //   console.warn("Duplicate detected! Payload");
      //   console.table(payload);      
      //   console.log("Full Payload:", payload); 
      //   setResponseMessage("This configuration already exists.");
      //   setAlertSeverity("error");
      //   setShowAlert(true);
      //   setTimeout(() => setShowAlert(false), 5000);
      //   return;
      // }
    }

    setLoading(true);
    try {
      console.log('Sending payload:', payload);
      if (isEdit) {
        await updateMonitoringConfigAPI(payload);
      } else {
        await saveMonitoringConfigAPI(payload);
      }
      await fetchMonitoringConfigs();
      resetForm();
      setResponseMessage(isEdit ? 'Configuration updated successfully.' : 'Configuration saved successfully.');
      setAlertSeverity("success");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
    } catch (error) {
      console.error('Error saving data:', error);
      setResponseMessage('Unable to save the data. Please try again.');
      setAlertSeverity("error");
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  // -------------------- Reset / Fetch / Edit --------------------
  const resetForm = () => {
    setSupplierSourcesSelected([]);
    setSupplierNamesSelected([]);
    setSupplierDetails([]);

    setFormData({
      departure: null,
      arrival: null,
      // frequency: '',
      // feq_smh: 'minute',   
      noOfRecords: '',
      responseThreshold: '',
      recordThreshold: '',
    });

    // default UI state
    setIsActive(true);
    setToggleChanged(false);

    setShowAlert(false);
    setIsEdit(false);
    setEditingRecord(null);
  };

  const fetchMonitoringConfigs = async () => {
    try {
      setLoading(true);
      const response = await fetchMonitoringConfigsAPI({ allRecords: true, product: 'Flight' });
      const rows = response?.data?.data || [];
      setMonitoringData(rows);
    } catch (err) {
      console.error('Failed to fetch monitoring configs:', err);
    } finally {
      setLoading(false);
    }
  }; 

  useEffect(() => {
    fetchMonitoringConfigs();
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = monitoringData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleEdit = (row: MonitoringConfig) => {
    setIsEdit(true);
    setInvalidFields([]);
    setEditingRecord(row);
    setSupplierSourcesSelected(row.supplierSource ? [row.supplierSource] : []);
    setSupplierNamesSelected(row.supplierName ? [row.supplierName] : []);
    // reconstruct supplierDetails so UI shows existing supplier chip/values
    setSupplierDetails([
      {
        supplierSource: row.supplierSource,
        supplierName: row.supplierName,
        supplierId: row.supplierId ?? 0,
      },
    ]);

    // helper function to clean code (remove " - " and everything after it)
  const cleanCode = (value?: string) =>
    value ? value.split(" -")[0].trim() : "";

    setFormData({
      departure: row.departure
      ? { code: cleanCode(row.departure), fullName: "" }
      : null,
    arrival: row.arrival
      ? { code: cleanCode(row.arrival), fullName: "" }
      : null,
      // frequency: row.frequency ?? '',
      // feq_smh
      // :row.feq_smh?.toLowerCase() === "second"
      // ? "second"
      // : row.feq_smh?.toLowerCase() === "minute"
      // ? "minute"
      // : row.feq_smh?.toLowerCase() === "hour"
      // ? "hour"
      // : "minute", 
      noOfRecords: row.noOfRecords ?? '',
      responseThreshold: row.responseThreshold ?? '',
      recordThreshold: row.recordThreshold ?? '',
    });
    setIsActive(Boolean(row.isActive));
    setToggleChanged(false); // reset when entering edit
  };

  // -------------------- UI --------------------
  return (
    <>
      {showAlert && (
        <Box className="alertWrap">
          <Alert
            severity={alertSeverity} // dynamic severity
            onClose={() => setShowAlert(false)}
            action={
              <IconButton aria-label="close" color="inherit" size="small" onClick={() => setShowAlert(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {responseMessage}
          </Alert>
        </Box>
      )}
      <Dialog open={openModal} onClose={handleCloseModal} className='PopupAlertMsg'>
        <CancelIcon onClick={handleCloseModal} className="closeModal" />
  <DialogTitle>Notification</DialogTitle>
  <DialogContent>
    <Typography></Typography>
  </DialogContent>
</Dialog>

      <Box className="headingBtnWrap">
        <Typography variant="h1">Flight Monitoring Setting</Typography>
      </Box>

      <Box className="feeInputFieldWrap horizontalWrap w44">
        {/* Supplier Source */}
        <div className="form-control inputFieldWrap">
          <label>Supplier Source</label>
          <FormControl sx={{ minWidth: 270 }} className={`commondropdownWrap ${invalidFields.includes("Supplier Source") ? "redBorder" : ""} ${isEdit ? "pointerEvents" : ""}`}>
            <Select
              id="supplier-source"
              className="commondropdown"
              multiple
              value={supplierSourcesSelected}
              onChange={handleSupplierSourceChange}
              displayEmpty
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if ((selected as string[]).length === 0) {
                return <span style={{ color: "#9e9e9e" }}>Select Source</span>; // gray placeholder
                }
                return (selected as string[]).join(", ");
                }}
              MenuProps={SupplierSourcesMP}
            >
              {supplierSources.map((src) => (
                <MenuItem key={src} value={src} className='selectDropdown'>
                  <Checkbox checked={supplierSourcesSelected.includes(src)} />
                  <ListItemText primary={src} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* Supplier Name */}
        <div className="form-control inputFieldWrap">
          <label>Supplier Name</label>
          <FormControl sx={{ minWidth: 270 }} className={`commondropdownWrap ${
    invalidFields.includes("Supplier Name") ? "redBorder" : ""
  } ${isEdit ? "pointerEvents" : ""}`}>
            <Select
              id="supplier-name"
              className="commondropdown"
              multiple
              value={supplierNamesSelected}
              onChange={handleSupplierNameChange}
              displayEmpty
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if ((selected as string[]).length === 0) {
                  return <span style={{ color: "#9e9e9e" }}>Select Supplier</span>; // gray placeholder
                }
                return (selected as string[]).join(", ");
                }}
                MenuProps={SupplierNames}
            >
              {supplierSourcesSelected.length === 0 ? (
                <MenuItem disabled>No supplier source selected</MenuItem>
              ) : (
                Array.from(
                  new Set(supplierSourcesSelected.flatMap((source) => supplierNamesMap[source] || []))
                ).map((name) => (
                  <MenuItem key={name} value={name} className='selectDropdown'>
                    <Checkbox checked={supplierNamesSelected.includes(name)} />
                    <ListItemText primary={name} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </div>

      {/* Departure Autocomplete */}
<div className="form-control inputFieldWrap departureArrivalWrap">
  <label>Departure</label>
  <FormControl sx={{ minWidth: 270 }} className={`commondropdownWrap ${
    invalidFields.includes("departure") ? "redBorder" : ""
  }`}>
    <Autocomplete
      options={departureOptions}
      getOptionLabel={(option) =>
        option.fullName ? `${option.code} - ${option.fullName}` : option.code
      }
      loading={loadingDeparture}
      value={formData.departure}
      onChange={(e, value) => {
        setFormData((prev) => ({
          ...prev,
          departure: value ? { code: value.code, fullName: value.fullName } : null,
        }));
        if (value) {
          // clear red border for departure when user selects a new value
          setInvalidFields((prev) => prev.filter((f) => f !== "departure"));
        }
        // Live validation: same Departure/Arrival not allowed
        if (
          value &&
          formData.arrival &&
          (
            value.code?.toUpperCase() === formData.arrival.code?.toUpperCase() ||
            value.fullName?.toLowerCase() === formData.arrival.fullName?.toLowerCase()
          )
        ) {
          setInvalidFields(["departure", "arrival"]);
          setResponseMessage("Departure and Arrival cannot be the same airport.");
          setAlertSeverity("error");
          setShowAlert(true);
          setTimeout(() => setShowAlert(false), 5000);
        } else {
          // Clear red borders if fixed
          setInvalidFields((prev) =>
            prev.filter((f) => f !== "departure" && f !== "arrival")
          );
        }
      }}
      onInputChange={(e, value) => {
                // fetch only when user has typed 3 or more chars
                if (value && value.length >= 3) {
                  fetchAirports(value, 'departure');
                }
              }}
      renderInput={(params) => (
        <TextField
        className="commondropdown"
          {...params}
          placeholder="Enter Airport Code"
          inputProps={{ 
            ...params.inputProps, 
            maxLength: 3 // limit input to 3 characters
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingDeparture ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  </FormControl>
</div>

{/* Arrival Autocomplete */}
<div className="form-control inputFieldWrap departureArrivalWrap">
  <label>Arrival</label>
  <FormControl sx={{ minWidth: 270 }} className={`commondropdownWrap ${
    invalidFields.includes("arrival") ? "redBorder" : ""
  }`}>
    <Autocomplete
      options={arrivalOptions}
      getOptionLabel={(option) =>
        option.fullName ? `${option.code} - ${option.fullName}` : option.code
      }
      loading={loadingArrival}
      value={formData.arrival}
      onChange={(e, value) => {
        setFormData((prev) => ({
          ...prev,
          arrival: value ? { code: value.code, fullName: value.fullName } : null,
        }));

        if (value) {
          // clear red border for arrival when user selects a new value
          setInvalidFields((prev) => prev.filter((f) => f !== "arrival"));
        }
        // Live validation: same Departure/Arrival not allowed
        if (
          value &&
          formData.departure &&
          (
            value.code?.toUpperCase() === formData.departure.code?.toUpperCase() ||
            value.fullName?.toLowerCase() === formData.departure.fullName?.toLowerCase()
          )
        ) {
          setInvalidFields(["departure", "arrival"]);
          setResponseMessage("Departure and Arrival cannot be the same airport.");
          setAlertSeverity("error");
          setShowAlert(true);
          setTimeout(() => setShowAlert(false), 5000);
        } else {
          // Clear red borders if fixed
          setInvalidFields((prev) =>
            prev.filter((f) => f !== "departure" && f !== "arrival")
          );
        }
      }}
      onInputChange={(e, value) => {
                if (value && value.length >= 3) {
                  fetchAirports(value, 'arrival');
                }
              }}
      renderInput={(params) => (
        <TextField
        className="commondropdown"
          {...params}
          placeholder="Enter Airport Code"
          inputProps={{ 
            ...params.inputProps, 
            maxLength: 3 // limit input to 3 characters
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingArrival ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  </FormControl>
</div>
{/* <div className="form-control inputFieldWrap">
  <label>Frequency</label>
  <input
    type="number"
    name="frequency"
    className={`inputField freqInput ${
    invalidFields.includes("frequency") ? "redBorder" : ""
  }`}
    value={formData.frequency}
    onChange={handleInputChange}
  />
  <FormControl sx={{ minWidth: 120 }} className="commondropdownWrap">
    <Select
      className="commondropdown freqdropdown"
      id="unit-select"
      value={formData.feq_smh}        
      onChange={(e) =>
    setFormData((prev) => ({ ...prev, feq_smh: e.target.value }))
  }
    >
      <MenuItem value="second">Second</MenuItem> 
      <MenuItem value="minute">Minute</MenuItem>
      <MenuItem value="hour">Hour</MenuItem>
    </Select>
  </FormControl>
          
</div> */}

<div className="form-control inputFieldWrap">
  <label>No. of Records</label>
  <input
    type="number"
    name="noOfRecords"
    className={`inputField ${
    invalidFields.includes("noOfRecords") ? "redBorder" : ""
  }`}
    value={formData.noOfRecords}
    onChange={handleInputChange}
  />
</div>

<div className="form-control inputFieldWrap">
  <label>Response Time (T.H)</label>
  <input
    type="number"
    name="responseThreshold"
    className={`inputField ${
    invalidFields.includes("responseThreshold") ? "redBorder" : ""
  }`}
    value={formData.responseThreshold}
    onChange={handleInputChange}
  />
</div>

<div className="form-control inputFieldWrap">
  <label>No. of Records (T.H)</label>
  <input
    type="number"
    name="recordThreshold"
    className={`inputField ${
    invalidFields.includes("recordThreshold") ? "redBorder" : ""
  }`}
    value={formData.recordThreshold}
    onChange={handleInputChange}
  />
</div>


        {/* Status */}
        <div className="form-control inputFieldWrap statusRevert">
          <label>Status</label>
          <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 270 }} className={`${isEdit ? "" : "pointerEvents"}`}>
            <BlackWhiteSwitches checked={isActive} onChange={handleToggle} />
          </Box>
        </div>

        {/* Health */}
        <div className="form-control inputFieldWrap healthWrap">
          <label>Health</label>
          <Accordion className="healthAccordian">
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
              <Box className="healthHeaderBox">
                <Box>0 to 3 sec</Box>
                <Box>Green</Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="healthBoxWrap">
                <Box className="healthBox">
                  <Box>4 to 5 sec</Box>
                  <Box>Yellow</Box>
                </Box>
                <Box className="healthBox">
                  <Box>6 to 7 sec</Box>
                  <Box>Orange</Box>
                </Box>
                <Box className="healthBox">
                  <Box>8 to 9 sec</Box>
                  <Box>Red</Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </div>

        {/* Save / Update */}
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            className="formBtn"
            onClick={handleSave}
            disabled={loading}
            style={{ backgroundColor: '#272727', color: '#fff', padding: '8px 14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {loading && (
              <CircularProgress
                size={20}
                thickness={5}
                sx={{ color: '#fff' }}
              />
            )}
            {isEdit ? 'Update' : 'Save'}
          </Button>
          
        </Box>
      </Box>

      {/* Table */}
      <Box mt={4}>
        <Paper sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Supplier Source</TableCell>
                  <TableCell align="center">Supplier Name</TableCell>
                  <TableCell align="center">Departure</TableCell>
                  <TableCell align="center">Arrival</TableCell>
                  {/* <TableCell align="center">Frequency</TableCell> */}
                  <TableCell align="center">No. of Records</TableCell>
                  <TableCell align="center">Response Time (T.H)</TableCell>
                  <TableCell align="center">No. of Records (T.H)</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No records found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>{row.supplierSource}</TableCell>
                      <TableCell align="center">{row.supplierName}</TableCell>
                      <TableCell align="center">{row.departure}</TableCell>
                      <TableCell align="center">{row.arrival}</TableCell>
                      {/* <TableCell align="center">{row.frequency} {row.feq_smh}</TableCell> */}
                      <TableCell align="center">{row.noOfRecords}</TableCell>
                      <TableCell align="center">{row.responseThreshold}</TableCell>
                      <TableCell align="center">{row.recordThreshold}</TableCell>
                      <TableCell align="center">
                        <Typography color={row.isActive ? 'green' : 'gray'}>
                          {row.isActive ? 'Active' : 'Inactive'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(row)}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={monitoringData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </>
  );
};

export default FlightMonitoringSetting;

